import type { Exercise } from './exercise'
import type {
  AfapPhase,
  AmrapPhase,
  Challenge,
  ChallengeWeek,
  CountdownPhase,
  IntervalsPhase,
  Movement,
  Phase,
  RichText,
  TimedMovement,
} from './workout'

/**
 * Convierte lo que llega de la base de datos en el modelo del dominio.
 *
 * Se valida a mano y sin dependencias: un reto mal formado no debe tumbar la
 * app de un alumno a mitad de agosto, así que ante la duda se devuelve null y
 * se sigue con el reto que la app trae incluido.
 */

type Json = Record<string, unknown>

const isObject = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const str = (value: unknown, fallback = ''): string => (typeof value === 'string' ? value : fallback)

const num = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

function parseRichText(value: unknown): RichText[] {
  return arr(value)
    .filter(isObject)
    .map((part) => {
      const text = str(part.text)
      return part.strong === true ? { text, strong: true } : { text }
    })
}

function parseMovements(value: unknown): Movement[] {
  return arr(value)
    .filter(isObject)
    .map((item) => ({ exerciseId: str(item.exerciseId), reps: num(item.reps) ?? 0 }))
    .filter((movement) => movement.exerciseId !== '')
}

function parseTimedMovements(value: unknown): TimedMovement[] {
  return arr(value)
    .filter(isObject)
    .map((item) => ({ exerciseId: str(item.exerciseId), seconds: num(item.seconds) ?? 0 }))
    .filter((movement) => movement.exerciseId !== '' && movement.seconds > 0)
}

function parsePhase(value: unknown): Phase | null {
  if (!isObject(value)) return null

  const base = {
    id: str(value.id),
    tag: str(value.tag),
    name: str(value.name),
    summary: str(value.summary),
    ...(typeof value.briefing === 'string' ? { briefing: value.briefing } : {}),
  }
  if (base.id === '' || base.name === '') return null

  switch (value.kind) {
    case 'intervals': {
      const pattern = parseTimedMovements(value.pattern)
      const rounds = num(value.rounds) ?? 1
      if (pattern.length === 0) return null
      const phase: IntervalsPhase = {
        ...base,
        kind: 'intervals',
        rounds,
        restSeconds: num(value.restSeconds) ?? 0,
        ...(num(value.roundRestSeconds) !== undefined
          ? { roundRestSeconds: num(value.roundRestSeconds) as number }
          : {}),
        pattern,
      }
      return phase
    }
    case 'amrap': {
      const duration = num(value.durationSeconds)
      const movements = parseMovements(value.movements)
      if (!duration || movements.length === 0) return null
      const phase: AmrapPhase = {
        ...base,
        kind: 'amrap',
        durationSeconds: duration,
        movements,
        ...(value.scored === true ? { scored: true } : {}),
      }
      return phase
    }
    case 'afap': {
      const blocks = parseMovements(value.blocks)
      if (blocks.length === 0) return null
      const phase: AfapPhase = {
        ...base,
        kind: 'afap',
        capSeconds: num(value.capSeconds) ?? 15 * 60,
        estimatedSeconds: num(value.estimatedSeconds) ?? 6 * 60,
        blocks,
      }
      return phase
    }
    case 'countdown': {
      const duration = num(value.durationSeconds)
      if (!duration) return null
      const phase: CountdownPhase = { ...base, kind: 'countdown', durationSeconds: duration }
      return phase
    }
    default:
      return null
  }
}

function parseWeek(value: unknown): ChallengeWeek | null {
  if (!isObject(value)) return null

  const number = num(value.number)
  const title = isObject(value.title) ? value.title : {}
  const workout = isObject(value.workout) ? value.workout : {}
  const phases = arr(workout.phases).map(parsePhase).filter((phase): phase is Phase => phase !== null)

  if (number === undefined || phases.length === 0) return null

  return {
    number,
    title: {
      ...(typeof title.lead === 'string' && title.lead !== '' ? { lead: title.lead } : {}),
      accent: str(title.accent),
    },
    dateLabel: str(value.dateLabel),
    startsOn: str(value.startsOn),
    blockLabel: str(value.blockLabel),
    intro: parseRichText(value.intro),
    workout: { id: str(workout.id, `semana-${number}`), phases },
    ...(num(value.benchmarkOf) !== undefined ? { benchmarkOf: num(value.benchmarkOf) as number } : {}),
  }
}

export function parseChallenge(value: unknown): Challenge | null {
  if (!isObject(value)) return null

  const weeks = arr(value.weeks)
    .map(parseWeek)
    .filter((week): week is ChallengeWeek => week !== null)
    .sort((a, b) => a.number - b.number)

  if (str(value.id) === '' || weeks.length === 0) return null

  const bonus = isObject(value.bonus)
    ? {
        title: arr(value.bonus.title).map((line) => str(line)),
        maxUses: num(value.bonus.maxUses) ?? 0,
        paragraphs: arr(value.bonus.paragraphs).map(parseRichText),
      }
    : undefined

  return {
    id: str(value.id),
    name: str(value.name),
    targetSessions: num(value.targetSessions) ?? 8,
    sessionsPerWeek: num(value.sessionsPerWeek) ?? 2,
    weeks,
    rules: arr(value.rules)
      .filter(isObject)
      .map((rule) => ({ title: str(rule.title), description: str(rule.description) })),
    prizes: arr(value.prizes)
      .filter(isObject)
      .map((prize) => ({
        sessions: num(prize.sessions) ?? 0,
        title: str(prize.title),
        description: str(prize.description),
      })),
    ...(bonus ? { bonus } : {}),
    footer: arr(value.footer).map((line) => str(line)),
  }
}

/** El catálogo de ejercicios que acompaña al reto, si viene informado. */
export function parseExercises(value: unknown): Record<string, Exercise> {
  if (!isObject(value)) return {}

  const catalog: Record<string, Exercise> = {}
  for (const [id, raw] of Object.entries(value)) {
    if (!isObject(raw)) continue
    catalog[id] = {
      id,
      name: str(raw.name, id),
      nameEs: str(raw.nameEs),
      cue: str(raw.cue),
    }
  }
  return catalog
}
